<script setup lang="ts">
import { computed } from 'vue';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LazyTooltip } from '@/components/ui/tooltip';
import { TrashIcon } from '@heroicons/vue/24/solid';
import type { IRule } from '@/types/filterBuilderTypes';

/**
 * The list search box's rule, as the filter builder shows it (SAU-467). It is a quick
 * search of the record type's chosen fields, not a rule on one column, so it gets its own
 * row: "Search box" and "contains" stay fixed, and only the text can be edited or the rule
 * deleted. Which fields it looks in is set in the search box's tag row.
 *
 * It fills the same four cells a RuleRow does, through `display: contents`, so it lines up
 * with the rules around it.
 */
defineEmits<{
  deleteRule: [ruleId: string];
}>();

const rule = defineModel<IRule>('rule', { required: true });

const text = computed({
  get: () => (typeof rule.value.value === 'string' ? rule.value.value : ''),
  set: (next: string) => {
    rule.value = { ...rule.value, value: next };
  },
});
</script>

<template>
  <div class="contents" data-testid="filter-rule-row" data-rule-kind="list-search">
    <div style="grid-column: field-start / field-end;" class="flex h-8 min-w-0 items-center px-2 text-xs">Search box</div>
    <div style="grid-column: op-start / op-end;" class="flex h-8 min-w-0 items-center px-2 text-xs text-muted-foreground">contains</div>
    <div style="grid-column: value-start / value-end;" class="min-w-0 w-full">
      <Input v-model="text" aria-label="Search text" data-testid="filter-list-search-input" class="h-8 w-full min-w-0 text-xs" />
    </div>
    <div style="grid-column: menu-start / menu-end;" class="flex justify-end gap-0.5 md:justify-self-end">
      <LazyTooltip>
        <Button
          size="icon"
          aria-label="Delete rule"
          class="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
          variant="ghost"
          data-testid="filter-delete-rule-button"
          @click="$emit('deleteRule', rule.id)"
        >
          <TrashIcon class="h-3.5 w-3.5" />
        </Button>
        <template #content>Delete rule</template>
      </LazyTooltip>
    </div>
  </div>
</template>
